import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CheckoutStep, StepProps } from "@/lib/types/checkout-steps";
import postnordLogo from "@/assets/postnord.png";
import refurblyLogo from "@/assets/refurbly-white.svg";
import { Info } from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { Spinner } from "../ui/spinner";
import { cn } from "@/lib/utils";
import { capitalizeEachWord } from "@/lib/utils/strings";
import {
  setSelectedServicePoint,
  setSelectedParcelBox,
  setDeliveryAddress,
  DeliveryMethodType,
  setSelectedDeliveryMethod,
} from "@/store/slices/delivery.slice";
import {
  validateAddress,
  createDeliveryAddress,
  fetchShippingMethods,
  fetchServicePoints,
  fetchParcelBoxes,
  fetchSelectDeliveryMethod,
} from "@/store/thunks/delivery.thunks";
import { fetchOrder } from "@/store/thunks/order.thunks";
import { trackAddShippingInfo } from "@/lib/analytics/ga4";
import {
  selectAddressValidation,
  selectDeliveryAddress,
  selectDeliveryLoadingStates,
  selectDeliveryMethod,
  selectParcelBoxes,
  selectServicePoints,
  selectShippingMethods,
  selectSPARv2Address,
} from "@/store/selectors/delivery.selectors";
import { selectFetchedOrder } from "@/store/selectors/order.selector";
import {
  selectBankIdContact,
  selectVerificationDetails,
} from "@/store/selectors/bankid.selectors";
import { useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

const DELIVERY_OPTION_IDS = [
  "servicePoint",
  "parcelBox",
  "pickup",
] as const;

type DeliveryOptionId = (typeof DELIVERY_OPTION_IDS)[number];

const DELIVERY_OPTION_SHIPPING_NAMES: Record<DeliveryOptionId, string> = {
  servicePoint: "Delivery to service point",
  parcelBox: "Delivery to mailbox",
  pickup: "Pick up in store",
};

function DeliveryOptionLogo({
  optionId,
  className,
}: {
  optionId: DeliveryOptionId;
  className?: string;
}) {
  if (optionId === "pickup") {
    return (
      <img
        src={refurblyLogo}
        alt="Refurbly"
        className={cn("h-5 w-auto object-contain brightness-0", className)}
      />
    );
  }

  if (optionId === "servicePoint" || optionId === "parcelBox") {
    return (
      <img
        src={postnordLogo}
        alt="PostNord"
        className={cn("h-3.5 w-auto object-contain", className)}
      />
    );
  }

  return null;
}

export function DeliveryStep({
  isActive,
  // isCompleted,
  onComplete,
}: StepProps<CheckoutStep.DELIVERY>) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const order = useAppSelector(selectFetchedOrder);
  const sparAddress = useAppSelector(selectSPARv2Address);
  const deliveryAddress = useAppSelector(selectDeliveryAddress);
  const deliveryMethod = useAppSelector(selectDeliveryMethod);
  const selectedDeliveryMethod = useAppSelector(selectDeliveryMethod);
  const shippingMethods = useAppSelector(selectShippingMethods);
  const servicePoints = useAppSelector(selectServicePoints);
  const parcelBoxes = useAppSelector(selectParcelBoxes);
  const addressValidation = useAppSelector(selectAddressValidation);

  const loadingStatus = useAppSelector(selectDeliveryLoadingStates);

  const verificationDetails = useAppSelector(selectVerificationDetails);
  const contact = useAppSelector(selectBankIdContact);

  const deliveryOptions = useMemo(
    () =>
      DELIVERY_OPTION_IDS.map((id) => ({
        id,
        title: t(`delivery.options.${id}`),
        description: t(`delivery.options.${id}Desc`),
        price: t("delivery.freePrice"),
        shippingMethodName: DELIVERY_OPTION_SHIPPING_NAMES[id],
      })),
    [t]
  );

  // Ref to track if delivery options have been fetched for current address/order
  // This prevents duplicate fetches and infinite loops
  const fetchedForAddressRef = useRef<string | null>(null);

  // Memoized handler to fetch delivery options
  const handleFetchDeliveryOptions = useCallback(async () => {
    if (!sparAddress?.isValid || !order?.id || !verificationDetails) return;

    // Create a unique key for the current address + order combination
    const addressKey = `${sparAddress.street}|${sparAddress.postalCode}|${sparAddress.city}|${order.id}`;

    // Skip if already fetched for this address/order
    if (fetchedForAddressRef.current === addressKey) {
      return;
    }

    // Mark as fetched before starting (optimistic)
    fetchedForAddressRef.current = addressKey;

    try {
      // Set the address from SPARv2
      dispatch(
        setDeliveryAddress({
          street: sparAddress.street,
          postalCode: sparAddress.postalCode,
          city: sparAddress.city,
        })
      );

      // Validate address
      await dispatch(
        validateAddress({
          streetName: sparAddress.street,
          postalCode: sparAddress.postalCode,
          city: sparAddress.city,
        })
      ).unwrap();

      // Create delivery address
      await dispatch(
        createDeliveryAddress({
          orderId: order.id,
          firstName: capitalizeEachWord(
            verificationDetails.userAttributes.givenName
          ),
          lastName: capitalizeEachWord(
            verificationDetails.userAttributes.surname
          ),
          street: capitalizeEachWord(sparAddress.street),
          city: capitalizeEachWord(sparAddress.city),
          postalCode: sparAddress.postalCode,
          phone: contact.phone,
          setAsBilling: true,
        })
      ).unwrap();

      // Fetch shipping methods
      await dispatch(fetchShippingMethods(order.id)).unwrap();

      // Only fetch service points initially since it's the default option
      await dispatch(
        fetchServicePoints({
          streetName: sparAddress.street,
          postalCode: sparAddress.postalCode,
          city: sparAddress.city,
        })
      );
    } catch (error) {
      console.error("Failed to fetch delivery options:", error);
      // Reset on error to allow retry
      fetchedForAddressRef.current = null;
    }
  }, [sparAddress, order?.id, verificationDetails, contact.phone, dispatch]);

  // Auto-fetch delivery options when component mounts if we have valid SPARv2 address
  // Use primitive values as dependencies to prevent infinite loops
  useEffect(() => {
    if (sparAddress?.isValid && order?.id) {
      handleFetchDeliveryOptions();
    }
  }, [
    sparAddress?.isValid,
    sparAddress?.street,
    sparAddress?.postalCode,
    sparAddress?.city,
    order?.id,
    handleFetchDeliveryOptions,
  ]);

  const handleDeliveryMethodChange = (value: DeliveryMethodType) => {
    if (!sparAddress?.street || !sparAddress?.postalCode || !sparAddress?.city)
      return;

    dispatch(setSelectedDeliveryMethod(value));

    // Only fetch parcel boxes if that option is selected and data isn't already loaded
    if (value === "parcelBox" && parcelBoxes.length === 0) {
      dispatch(
        fetchParcelBoxes({
          streetName: sparAddress.street,
          postalCode: sparAddress.postalCode,
          city: sparAddress.city,
        })
      );
    }
  };

  const handleDeliverySelection = async () => {
    if (
      !order?.id ||
      !selectedDeliveryMethod.selectedMethod ||
      !shippingMethods
    )
      return;

    try {
      // Map delivery method to shipping method name
      const methodNameMap = {
        servicePoint: "Delivery to service point",
        parcelBox: "Delivery to mailbox",
        pickup: "Pick up in store",
      };

      const shippingMethodName =
        methodNameMap[selectedDeliveryMethod.selectedMethod];
      const shippingMethod = shippingMethods.methods.find(
        (method) => method.name === shippingMethodName
      );

      if (!shippingMethod) {
        throw new Error(
          `No shipping method found for ${selectedDeliveryMethod.selectedMethod}`
        );
      }

      // Prepare metadata based on delivery method
      let metadata: Record<string, string> = {
        deliveryMethod: selectedDeliveryMethod.selectedMethod,
      };

      // Add service point/parcel box metadata if applicable
      if (
        selectedDeliveryMethod.selectedMethod === "servicePoint" &&
        selectedDeliveryMethod.selectedServicePoint
      ) {
        metadata = {
          ...metadata,
          "Service Point ID":
            selectedDeliveryMethod.selectedServicePoint.servicePointId,
          "Service Point Name":
            selectedDeliveryMethod.selectedServicePoint.name,
          "Service Point Street":
            selectedDeliveryMethod.selectedServicePoint.deliveryAddress
              .streetName,
          "Service Point Street Number":
            selectedDeliveryMethod.selectedServicePoint.deliveryAddress
              .streetNumber,
          "Service Point City":
            selectedDeliveryMethod.selectedServicePoint.deliveryAddress.city,
          "Service Point Postal Code":
            selectedDeliveryMethod.selectedServicePoint.deliveryAddress
              .postalCode,
          "Service Point Type": "servicepoint",
        };
      } else if (
        selectedDeliveryMethod.selectedMethod === "parcelBox" &&
        selectedDeliveryMethod.selectedParcelBox
      ) {
        metadata = {
          ...metadata,
          "Service Point ID":
            selectedDeliveryMethod.selectedParcelBox.servicePointId,
          "Service Point Name": selectedDeliveryMethod.selectedParcelBox.name,
          "Service Point Street":
            selectedDeliveryMethod.selectedParcelBox.deliveryAddress.streetName,
          "Service Point Street Number":
            selectedDeliveryMethod.selectedParcelBox.deliveryAddress
              .streetNumber,
          "Service Point City":
            selectedDeliveryMethod.selectedParcelBox.deliveryAddress.city,
          "Service Point Postal Code":
            selectedDeliveryMethod.selectedParcelBox.deliveryAddress.postalCode,
          "Service Point Type": "paketbox",
        };
      }

      // For pick up in store, we don't add any service point metadata
      // Preserve only non-service point related metadata
      const existingMetadata = order.metadata || {};
      const preservedMetadata = Object.entries(existingMetadata)
        .filter(([key]) => !key.startsWith("Service Point"))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      const finalMetadata =
        selectedDeliveryMethod.selectedMethod === "pickup"
          ? preservedMetadata
          : { ...preservedMetadata, ...metadata };

      await dispatch(
        fetchSelectDeliveryMethod({
          orderId: order.id,
          shipmentId: shippingMethods.shipmentId,
          shippingMethodId: shippingMethod.id,
          deliveryMethod: selectedDeliveryMethod.selectedMethod,
          metadata: finalMetadata,
        })
      ).unwrap();

      // Fetch the updated order
      await dispatch(fetchOrder(order.id));

      trackAddShippingInfo(order, selectedDeliveryMethod.selectedMethod);

      onComplete({
        deliveryMethod: selectedDeliveryMethod.selectedMethod,
        selectedServicePoint: selectedDeliveryMethod.selectedServicePoint,
        selectedParcelBox: selectedDeliveryMethod.selectedParcelBox,
        address: deliveryAddress,
      });
    } catch (error) {
      console.error("Failed to select delivery method:", error);
    }
  };

  if (!isActive) return null;

  if (!sparAddress?.isValid) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-600 font-medium">{t("delivery.sparError")}</p>
      </div>
    );
  }

  const finiliseDeliveryLoading = loadingStatus.selectMethod === "pending";

  const servicePointsLoading = loadingStatus.servicePoints;
  const parcelBoxesLoading = loadingStatus.parcelBoxes;

  return (
    <div>
      {/* Address Summary */}
      <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="font-medium">{t("delivery.sparTitle")}</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 rounded-full p-1.5 -m-1"
                aria-label={t("delivery.sparInfoAria")}
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="max-w-[280px] text-left font-normal"
            >
              <p>{t("delivery.sparBody")}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <p>{sparAddress.street}</p>
        <p>
          {sparAddress.postalCode} {sparAddress.city}
        </p>
      </div>

      {/* Delivery Options - Same style mobile & desktop */}
      <div className="mt-5 sm:mt-6">
        <Label htmlFor="delivery-method" className="text-base font-semibold text-gray-900 block mb-3 sm:mb-4">
          {t("delivery.chooseMethod")}
        </Label>
        <RadioGroup
          className="mt-0"
          id="delivery-method"
          value={deliveryMethod.selectedMethod || undefined}
          onValueChange={handleDeliveryMethodChange}
        >
          <div className="border rounded-10 overflow-hidden">
            {deliveryOptions.filter(
              (option) =>
                option.id === "servicePoint" ||
                option.id === "parcelBox" ||
                option.id === "pickup"
            ).map((option, index) => {
              const isSelected = selectedDeliveryMethod.selectedMethod === option.id;
              return (
                <div key={option.id}>
                  {index > 0 && (
                    <div className="h-px bg-gray-200 mx-3 sm:mx-5" aria-hidden />
                  )}
                  <div className="px-3 py-3 sm:px-5 sm:py-5">
                    <Label
                      htmlFor={option.id}
                      className="flex items-start gap-3 cursor-pointer sm:items-center"
                    >
                      <RadioGroupItem value={option.id} id={option.id} className="flex-shrink-0 mt-1 sm:mt-0" />
                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:justify-between sm:items-center sm:gap-3 sm:w-full">
                        <div className="flex items-start gap-3 min-w-0 sm:flex-1 sm:items-center">
                          <div className="min-w-0 flex-1">
                            <div className="flex justify-between items-start gap-3">
                              <span className="font-semibold text-[15px] text-gray-900 sm:font-medium leading-tight">
                                {option.title}
                              </span>
                              <div className="flex items-center gap-1.5 flex-shrink-0 sm:hidden">
                                <DeliveryOptionLogo
                                  optionId={option.id}
                                  className={
                                    option.id === "pickup" ? "h-3.5" : "h-2.5"
                                  }
                                />
                                <span className="text-sm font-medium text-gray-600">
                                  {option.price}
                                </span>
                              </div>
                            </div>
                            <p className="text-[13px] text-gray-600 font-medium mt-1.5 leading-relaxed sm:text-sm sm:mt-0.5">
                              {option.description}
                            </p>
                          </div>
                        </div>
                        <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
                          <DeliveryOptionLogo optionId={option.id} />
                          <span className="text-sm font-medium text-gray-600">
                            {option.price}
                          </span>
                        </div>
                      </div>
                    </Label>

                    {/* Service Point Selector */}
                    {isSelected && option.id === "servicePoint" &&
                      (servicePointsLoading === "pending" ||
                      (servicePointsLoading === "idle" && servicePoints.length === 0) ? (
                        <div className="flex justify-center py-6 sm:py-5">
                          <Spinner size="sm" />
                        </div>
                      ) : (
                        <div className="mt-3 sm:mt-5 w-full text-left pl-7 sm:pl-8">
                          <Select
                            value={selectedDeliveryMethod.selectedServicePoint?.servicePointId}
                            onValueChange={(value) => {
                              const point = servicePoints.find((p) => p.servicePointId === value);
                              dispatch(setSelectedServicePoint(point || null));
                            }}
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full min-h-11 h-auto sm:min-h-[44px] bg-white [&>span]:!line-clamp-none py-2.5 px-3 sm:py-3 sm:px-4 sm:mt-0 items-center justify-between gap-2 text-left [&>span]:flex-1 [&>span]:min-w-0 [&>span]:text-left",
                                selectedDeliveryMethod.selectedServicePoint
                                  ? "border border-gray-200"
                                  : "border-2 border-primary"
                              )}
                            >
                              <SelectValue placeholder={t("delivery.selectServicePoint")} />
                            </SelectTrigger>
                            <SelectContent>
                              {servicePoints.map((point) => {
                                const addressLine = `${point.deliveryAddress.streetName} ${point.deliveryAddress.streetNumber}, ${point.deliveryAddress.postalCode} ${capitalizeEachWord(point.deliveryAddress.city)}`;
                                return (
                                  <SelectItem key={point.servicePointId} value={point.servicePointId} className="py-3">
                                    <span className="block">
                                      <span className="font-medium block">{capitalizeEachWord(point.name)}</span>
                                      <span className="text-sm text-gray-500">{addressLine}</span>
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}

                    {/* Parcel Box Selector */}
                    {isSelected && option.id === "parcelBox" &&
                      (parcelBoxesLoading === "pending" ||
                      (parcelBoxesLoading === "idle" && parcelBoxes.length === 0) ? (
                        <div className="flex justify-center py-6 sm:py-5">
                          <Spinner size="sm" />
                        </div>
                      ) : (
                        <div className="mt-3 sm:mt-5 w-full text-left pl-7 sm:pl-8">
                          <Select
                            value={
                              selectedDeliveryMethod.selectedParcelBox?.servicePointId
                            }
                            onValueChange={(value) => {
                              const box = parcelBoxes.find((p) => p.servicePointId === value);
                              dispatch(setSelectedParcelBox(box || null));
                            }}
                          >
                            <SelectTrigger
                              className={cn(
                                "w-full min-h-11 h-auto sm:min-h-[44px] bg-white [&>span]:!line-clamp-none py-2.5 px-3 sm:py-3 sm:px-4 sm:mt-0 items-center justify-between gap-2 text-left [&>span]:flex-1 [&>span]:min-w-0 [&>span]:text-left",
                                selectedDeliveryMethod.selectedParcelBox
                                  ? "border border-gray-200"
                                  : "border-2 border-primary"
                              )}
                            >
                              <SelectValue placeholder={t("delivery.selectParcelBox")} />
                            </SelectTrigger>
                            <SelectContent>
                              {parcelBoxes.map((box) => {
                                const addressLine = `${box.deliveryAddress.streetName} ${box.deliveryAddress.streetNumber}, ${box.deliveryAddress.postalCode} ${capitalizeEachWord(box.deliveryAddress.city)}`;
                                return (
                                  <SelectItem key={box.servicePointId} value={box.servicePointId} className="py-3">
                                    <span className="block">
                                      <span className="font-medium block">{capitalizeEachWord(box.name)}</span>
                                      <span className="text-sm text-gray-500">{addressLine}</span>
                                    </span>
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        </RadioGroup>
        <Button
          className="w-full mt-5 sm:mt-6 min-h-[48px] text-base font-semibold"
          variant={"secondary"}
          size={"lg"}
          onClick={handleDeliverySelection}
          disabled={
            !selectedDeliveryMethod.selectedMethod ||
            (selectedDeliveryMethod.selectedMethod === "servicePoint" &&
              !selectedDeliveryMethod.selectedServicePoint) ||
            (selectedDeliveryMethod.selectedMethod === "parcelBox" &&
              !selectedDeliveryMethod.selectedParcelBox) ||
            finiliseDeliveryLoading
          }
        >
          {t("delivery.continueToPayment")}{" "}
          {finiliseDeliveryLoading && <Spinner size="sm" />}
        </Button>
      </div>

      {/* Error Message */}
      {addressValidation.error && (
        <div className="text-sm text-red-500 mt-2">
          {addressValidation.error}
        </div>
      )}
    </div>
  );
}
